/**
 * The Leaderboards tab.
 *
 * Owner (2026-09-24): "Leaderboards doesn't change, make it a separate tab, more
 * visualised … what is the difference between richest farmer and most
 * experienced, it all shows coins … it should show the fight statistics, who is
 * better, more fights."
 *
 * So each board says what it measures and in its own unit (XP, Coins, arena
 * Rating), every row carries the player's level, and the arena board shows wins,
 * losses, fights and win rate. Movement is the server's: places climbed and value
 * gained since the last hourly update, so a board visibly changes between visits
 * instead of silently reordering.
 */
import { el, clear } from '../dom';
import { boardsApi, type BoardDef, type BoardPage, type BoardRow } from '../game-api';
import { ApiError } from '../../core/network/api';
import type { GameCtx, ViewTeardown } from '../types';
import { gicon, type IconName } from '../ui/icons';
import { boardIcon, fmtCountdown, fmtGain, moveLabel, winRatePct } from './leaderboards/board-logic';

const PANEL = 'game-font rounded-xl border-2 border-[#5a4632] bg-surface/95 shadow-lift overflow-hidden';
const STORE_KEY = 'tb.boards.tab';
const MEDAL = ['#ffd24a', '#cfd6e0', '#d98a4a'];

export function renderLeaderboards(root: HTMLElement, ctx: GameCtx): ViewTeardown {
  clear(root);
  const tabsHost = el('div', 'flex gap-2 flex-wrap mb-3');
  const blurb = el('div', 'text-xs text-text-dim mb-3 min-h-[16px]');
  const meHost = el('div', 'mb-3');
  const podium = el('div', 'grid grid-cols-3 gap-2 sm:gap-3 mb-3 items-end');
  const movers = el('div', 'mb-3');
  const list = el('div', `${PANEL} divide-y divide-[#5a4632]/50`);
  const clock = el('span', 'text-[11px] text-text-dim font-mono');

  root.append(el('div', 'max-w-3xl mx-auto pb-8', {},
    el('div', 'flex items-baseline justify-between gap-2 mb-3 flex-wrap', {},
      el('h2', 'game-font text-lg text-[#ffd97a] inline-flex items-center gap-2', {}, gicon('trophy', 18), 'Leaderboards'),
      clock),
    tabsHost, blurb, meHost, podium, movers, list));

  let boards: BoardDef[] = [];
  let active = 'xp';
  try { active = localStorage.getItem(STORE_KEY) ?? 'xp'; } catch { /* private mode */ }
  let page: BoardPage | null = null;
  let alive = true;

  const board = () => boards.find((b) => b.key === active) ?? boards[0];

  function paintTabs() {
    clear(tabsHost);
    for (const b of boards) {
      const on = b.key === active;
      tabsHost.append(el('button',
        `min-h-[44px] px-3 rounded-lg border-2 text-sm inline-flex items-center gap-1.5 ${on
          ? 'border-[#e8c15a] bg-[#3a2c1e] text-[#ffd97a]' : 'border-[#5a4632] text-text-dim hover:text-text'}`,
        { type: 'button', onClick: () => { active = b.key; try { localStorage.setItem(STORE_KEY, active); } catch { /* */ } void load(); } },
        gicon(boardIcon(b.key), 14), b.label));
    }
  }

  function valueChip(r: BoardRow, big = false) {
    const b = board();
    const icon: IconName = boardIcon(b.key);
    return el('span', `inline-flex items-center gap-1 font-mono tabular-nums ${big ? 'text-base' : 'text-sm'} text-[#ffd97a]`, {},
      gicon(icon, big ? 14 : 12), r.value.toLocaleString(), b.key === 'arena' ? el('span', 'text-[10px] text-text-dim', {}, 'rating') : '');
  }

  function moveBadge(r: BoardRow) {
    const m = moveLabel(r.moved);
    const tone = m.kind === 'up' ? 'text-leaf' : m.kind === 'down' ? 'text-error' : m.kind === 'new' ? 'text-shroom' : 'text-text-dim';
    return el('span', `text-[11px] font-mono w-9 text-center shrink-0 ${tone}`, { title: m.title }, m.text);
  }

  function onlineDot(r: BoardRow) {
    return el('span', `inline-block w-2 h-2 rounded-full shrink-0 ${r.online ? 'bg-leaf' : 'bg-[#5a4632]'}`,
      { title: r.online ? 'Played in the last hour' : 'Not seen in the last hour' });
  }

  function fightLine(r: BoardRow) {
    if (!r.arena) return null;
    const a = r.arena;
    return el('div', 'text-[11px] text-text-dim font-mono', {},
      `${a.wins} W · ${a.losses} L · ${a.matches} fights · ${winRatePct(a.winRate)} wins`);
  }

  function paintMe() {
    clear(meHost);
    if (!page) return;
    const me = page.me;
    const b = board();
    // Staff accounts are never ranked (the same rule as core's boards) — say why,
    // or an admin testing the game reads "Not ranked" as a bug.
    const where = me.rank ? `#${me.rank.toLocaleString()} of ${me.total.toLocaleString()}`
      : ctx.user.role === 'ADMIN' ? 'Admin accounts are not ranked'
        : b.key === 'arena' ? 'Fight in the arena to be ranked' : 'Not ranked';
    meHost.append(el('div', `${PANEL} px-3 py-2.5 flex items-center justify-between gap-3 flex-wrap`, {},
      el('div', 'text-sm', {}, el('span', 'text-text-dim mr-2', {}, 'You'), el('b', 'text-[#ffd97a]', {}, ctx.user.username ?? 'you')),
      el('div', 'text-sm text-text-dim', {}, where),
      el('span', 'inline-flex items-center gap-1 font-mono text-[#ffd97a]', {}, gicon(boardIcon(b.key), 12), me.value.toLocaleString())));
  }

  function paintPodium() {
    clear(podium);
    if (!page) return;
    const top = page.rows.slice(0, 3);
    // 2nd, 1st, 3rd — the classic podium, tallest in the middle.
    const order = [top[1], top[0], top[2]];
    const heights = ['min-h-[118px]', 'min-h-[142px]', 'min-h-[104px]'];
    order.forEach((r, i) => {
      if (!r) { podium.append(el('div')); return; }
      const place = r.rank - 1;
      podium.append(el('div',
        `${PANEL} ${heights[i]} p-2 sm:p-3 flex flex-col items-center justify-end text-center gap-1`,
        { style: `border-color:${MEDAL[place]}` },
        el('div', 'text-2xl leading-none font-mono', { style: `color:${MEDAL[place]}` }, `#${r.rank}`),
        el('div', 'flex items-center gap-1 max-w-full', {}, onlineDot(r), el('span', 'text-sm truncate', { title: r.username }, r.username)),
        el('div', 'text-[10px] text-text-dim', {}, `Level ${r.level}`),
        valueChip(r, true),
        moveBadge(r),
        fightLine(r) ?? ''));
    });
  }

  function paintMovers() {
    clear(movers);
    if (!page?.since) return;
    const climbers = page.rows.filter((r) => (r.moved ?? 0) > 0).sort((a, b) => (b.moved ?? 0) - (a.moved ?? 0)).slice(0, 3);
    if (!climbers.length) return;
    movers.append(el('div', `${PANEL} px-3 py-2 text-xs flex items-center gap-x-4 gap-y-1 flex-wrap`, {},
      el('span', 'text-[#ffd97a]', {}, 'Climbing this hour'),
      ...climbers.map((r) => el('span', 'inline-flex items-center gap-1', {},
        el('span', 'text-leaf font-mono', {}, `▲${r.moved}`), r.username,
        r.gained ? el('span', 'text-text-dim font-mono', {}, fmtGain(r.gained)) : ''))));
  }

  function paintList() {
    clear(list);
    if (!page) return;
    const max = Math.max(1, page.rows[0]?.value ?? 1);
    for (const r of page.rows.slice(3)) {
      const mine = r.userId === ctx.user.id;
      const pct = Math.max(2, Math.round((r.value / max) * 100));
      list.append(el('div', `px-3 py-2 flex items-center gap-2 ${mine ? 'bg-[#3a2c1e]' : ''}`, {},
        el('span', 'w-9 text-right font-mono text-sm text-text-dim shrink-0', {}, `#${r.rank}`),
        moveBadge(r),
        onlineDot(r),
        el('div', 'flex-1 min-w-0', {},
          el('div', 'flex items-center gap-2 min-w-0', {},
            el('span', `truncate text-sm ${mine ? 'text-[#ffd97a]' : ''}`, { title: r.username }, r.username),
            el('span', 'text-[10px] px-1.5 rounded border border-[#5a4632] text-text-dim shrink-0', {}, `Lv ${r.level}`)),
          el('div', 'h-1.5 mt-1 rounded bg-[#241b14] overflow-hidden', {},
            el('div', 'h-full rounded bg-[#e8c15a]/70', { style: `width:${pct}%` })),
          fightLine(r) ?? ''),
        el('div', 'flex flex-col items-end shrink-0', {},
          valueChip(r),
          r.gained ? el('span', `text-[10px] font-mono ${r.gained > 0 ? 'text-leaf' : 'text-error'}`, {}, fmtGain(r.gained)) : '')));
    }
    if (page.rows.length <= 3) list.append(el('div', 'px-3 py-4 text-xs text-text-dim text-center', {}, 'No one else ranked yet.'));
  }

  async function load() {
    paintTabs();
    const b = board();
    if (!b) return;
    blurb.textContent = b.blurb;
    try {
      const next = await boardsApi.get(b.key);
      if (!alive || board()?.key !== b.key) return;
      page = next;
      paintMe(); paintPodium(); paintMovers(); paintList();
    } catch (e) {
      clear(list);
      list.append(el('div', 'px-3 py-4 text-xs text-error text-center', {},
        e instanceof ApiError ? e.message : 'Could not load the leaderboard.'));
    }
  }

  // Countdown to the next hourly update, and a quiet refresh every minute so a
  // tab left open keeps up with real players between updates.
  const tick = window.setInterval(() => {
    if (page) clock.textContent = `Next update in ${fmtCountdown(new Date(page.nextUpdate).getTime() - Date.now())}`;
  }, 1000);
  const refresh = window.setInterval(() => { void load(); }, 60_000);

  void (async () => {
    try { boards = await boardsApi.list(); } catch { boards = []; }
    if (!boards.some((b) => b.key === active)) active = boards[0]?.key ?? 'xp';
    await load();
  })();

  return () => { alive = false; window.clearInterval(tick); window.clearInterval(refresh); };
}
